import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  Search, PawPrint, User, Download, Printer,
  Stethoscope, Syringe, Calendar, FileText, ChevronDown, ChevronUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { getPets } from '../services/petService';
import { getExaminations } from '../services/examinationService';
import { getVaccinations } from '../services/vaccinationService';
import { getAppointments } from '../services/appointmentService';
import { Pet, Examination, Vaccination, Appointment } from '../types';
import { escapeHtml } from '../utils/htmlUtils';

const formatDate = (d?: string) => {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric' });
};

const displayAge = (ageInMonths: number) => {
  if (!ageInMonths || ageInMonths <= 0 || ageInMonths > 10000) return 'Belirtilmemiş';
  if (ageInMonths < 12) return `${ageInMonths} aylık`;
  const y = Math.floor(ageInMonths / 12);
  const m = ageInMonths % 12;
  return m === 0 ? `${y} yaşında` : `${y} yaş ${m} aylık`;
};

const translateSpecies = (s: string) => {
  const map: Record<string, string> = { cat: 'Kedi', dog: 'Köpek', bird: 'Kuş', rabbit: 'Tavşan', hamster: 'Hamster', fish: 'Balık', turtle: 'Kaplumbağa', other: 'Diğer' };
  return map[s?.toLowerCase()] || s;
};

const getStatusLabel = (status: string) => {
  const map: Record<string, string> = { scheduled: 'Planlandı', completed: 'Tamamlandı', cancelled: 'İptal', 'no-show': 'Gelmedi', pending: 'Bekliyor', overdue: 'Gecikmiş' };
  return map[status] || status;
};

type TimelineItem =
  | { type: 'examination'; date: string; data: Examination }
  | { type: 'vaccination'; date: string; data: Vaccination }
  | { type: 'appointment'; date: string; data: Appointment };

const PatientReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPet, setSelectedPet] = useState<Pet | null>(null);
  const [expandedSections, setExpandedSections] = useState({ examinations: true, vaccinations: true, appointments: true });
  const printRef = useRef<HTMLDivElement>(null);

  const { data: pets = [], isLoading: petsLoading } = useQuery<Pet[]>({ queryKey: ['pets'], queryFn: getPets });
  const { data: examinations = [] } = useQuery<Examination[]>({ queryKey: ['examinations'], queryFn: getExaminations });
  const { data: vaccinations = [] } = useQuery<Vaccination[]>({ queryKey: ['vaccinations'], queryFn: getVaccinations });
  const { data: appointments = [] } = useQuery<Appointment[]>({ queryKey: ['appointments'], queryFn: getAppointments });

  const filteredPets = pets.filter(p => {
    if (!searchTerm.trim()) return true;
    const s = searchTerm.toLowerCase();
    return p.name.toLowerCase().includes(s) || p.owner?.name?.toLowerCase().includes(s) || p.breed?.toLowerCase().includes(s);
  });

  const petExaminations = selectedPet ? examinations.filter(e => e.pet_id === selectedPet.id || e.petId === selectedPet.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];
  const petVaccinations = selectedPet ? vaccinations.filter(v => v.pet_id === selectedPet.id || v.petId === selectedPet.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];
  const petAppointments = selectedPet ? appointments.filter(a => a.pet_id === selectedPet.id || a.petId === selectedPet.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()) : [];

  // Kronolojik zaman çizelgesi
  const timeline: TimelineItem[] = [
    ...petExaminations.map(e => ({ type: 'examination' as const, date: e.date, data: e })),
    ...petVaccinations.map(v => ({ type: 'vaccination' as const, date: v.date, data: v })),
    ...petAppointments.map(a => ({ type: 'appointment' as const, date: a.date, data: a })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleDownloadPDF = () => {
    const input = printRef.current;
    if (!input || !selectedPet) return;
    toast.loading('PDF oluşturuluyor...', { id: 'pdf' });

    // Başlık div'ini geçici olarak göster
    const headerEl = input.querySelector('.pdf-only-header') as HTMLElement | null;
    if (headerEl) headerEl.style.display = 'block';

    const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--color-bg-card').trim() || '#ffffff';
    html2canvas(input, {
      scale: 2,
      backgroundColor: bgColor,
      useCORS: true,
      onclone: (_doc, clonedEl) => {
        // Butonları ve no-print elemanları gizle
        clonedEl.querySelectorAll('.no-print').forEach(el => {
          (el as HTMLElement).style.display = 'none';
        });
        // PDF başlığını göster
        const h = clonedEl.querySelector('.pdf-only-header') as HTMLElement | null;
        if (h) h.style.display = 'block';
      },
    }).then(canvas => {
      if (headerEl) headerEl.style.display = 'none';
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pw = pdf.internal.pageSize.getWidth();
      const imgWidth = pw;
      const imgHeight = (canvas.height * pw) / canvas.width;

      // Sayfalama
      const pageH = pdf.internal.pageSize.getHeight();
      let y = 0;
      while (y < imgHeight) {
        if (y > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, -y, imgWidth, imgHeight);
        y += pageH;
      }

      pdf.save(`${selectedPet.name}-hasta-gecmisi.pdf`);
      toast.success('PDF indirildi!', { id: 'pdf' });
    }).catch(() => {
      if (headerEl) headerEl.style.display = 'none';
      toast.error('PDF hatası', { id: 'pdf' });
    });
  };

  const handlePrint = () => {
    if (!printRef.current || !selectedPet) return;

    // Klonu al — no-print elemanları kaldır, pdf-only-header'ı göster
    const clone = printRef.current.cloneNode(true) as HTMLElement;
    clone.querySelectorAll('.no-print').forEach(el => el.remove());
    const pdfHeader = clone.querySelector('.pdf-only-header') as HTMLElement | null;
    if (pdfHeader) pdfHeader.style.display = 'block';

    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`
      <html>
        <head>
          <title>Hasta Raporu - ${escapeHtml(selectedPet.name)}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: system-ui, -apple-system, sans-serif; padding: 24px 32px; color: #111827; background: #fff; }
            h1, h2, h3 { margin: 0 0 8px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
            thead { background: #f9fafb; }
            th { text-align: left; padding: 8px 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; color: #6b7280; border-bottom: 1px solid #e5e7eb; }
            td { padding: 8px 12px; font-size: 13px; border-bottom: 1px solid #f3f4f6; }
            .dribbble-panel { border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; margin-bottom: 16px; background: #fff; }
            .space-y-4 > * + * { margin-top: 16px; }
            .grid { display: grid; gap: 12px; }
            .grid-cols-3 { grid-template-columns: repeat(3, 1fr); }
            .grid-cols-2 { grid-template-columns: repeat(2, 1fr); }
            .grid-cols-4 { grid-template-columns: repeat(4, 1fr); }
            .p-4 { padding: 16px; }
            .p-6 { padding: 24px; }
            .text-center { text-align: center; }
            .text-2xl { font-size: 22px; font-weight: 700; }
            .text-xs { font-size: 11px; color: #6b7280; }
            .text-sm { font-size: 13px; }
            .font-semibold { font-weight: 600; }
            .font-bold { font-weight: 700; }
            .text-[#2B2B2B] { color: #111827; }
            .text-[#8A8676] { color: #6b7280; }
            .text-[#B0A99A] { color: #9ca3af; }
            .border-t { border-top: 1px solid #e5e7eb; }
            .pt-4 { padding-top: 16px; }
            .mb-4 { margin-bottom: 16px; }
            .mt-1 { margin-top: 4px; }
            .px-2 { padding-left: 8px; padding-right: 8px; }
            .py-1 { padding-top: 4px; padding-bottom: 4px; }
            .rounded-full { border-radius: 9999px; }
            .bg-green-100 { background: #dcfce7; }
            .text-green-700 { color: #15803d; }
            .bg-[#FAECE7] { background: #fef3c7; }
            .text-amber-700 { color: #b45309; }
            .bg-[#E8EFD9] { background: #dbeafe; }
            .text-[#3F5728] { color: #1d4ed8; }
            .bg-red-100 { background: #fee2e2; }
            .text-red-700 { color: #b91c1c; }
            .no-print { display: none !important; }
            .pdf-only-header { display: block !important; }
          </style>
        </head>
        <body>${clone.innerHTML}</body>
      </html>
    `);
    w.document.close();
    setTimeout(() => { w.print(); w.close(); }, 600);
  };

  return (
    <div className="space-y-6 pb-8">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2B2B]">Hasta Geçmişi Raporu</h1>
          <p className="text-[#8A8676] mt-1">Muayene, aşı ve randevu geçmişini tek sayfada görüntüle</p>
        </div>
        {selectedPet && (
          <div className="no-print flex gap-2">
            <button onClick={handleDownloadPDF} className="btn btn-secondary flex items-center gap-2">
              <Download className="w-4 h-4" /> PDF İndir
            </button>
            <button onClick={handlePrint} className="btn btn-secondary flex items-center gap-2">
              <Printer className="w-4 h-4" /> Yazdır
            </button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sol: Hasta Listesi */}
        <div className="lg:col-span-1">
          <div className="dribbble-panel p-4 sticky top-24">
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B0A99A] w-4 h-4" />
              <input
                type="text"
                placeholder="Hasta veya sahip ara..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="input pl-9 text-sm"
              />
            </div>
            <div className="max-h-[calc(100vh-280px)] overflow-y-auto space-y-1">
              {petsLoading ? (
                <p className="text-center py-4 text-[#B0A99A] text-sm">Yükleniyor...</p>
              ) : filteredPets.map(pet => {
                const isSelected = selectedPet?.id === pet.id;
                const examCount = examinations.filter(e => e.pet_id === pet.id || e.petId === pet.id).length;
                const vaccCount = vaccinations.filter(v => v.pet_id === pet.id || v.petId === pet.id).length;
                return (
                  <div
                    key={pet.id}
                    onClick={() => setSelectedPet(pet)}
                    className={`p-3 rounded-xl cursor-pointer transition-all border ${
                      isSelected
                        ? 'bg-[#E8EFD9] border-[#A9B98A] shadow-sm'
                        : 'border-transparent hover:bg-[#F7F4ED] hover:border-[#E8E2D4]'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${pet.species === 'dog' ? 'bg-[#FAECE7]' : 'bg-[#F0EBE1]'}`}>
                        <PawPrint className={`w-4 h-4 ${pet.species === 'dog' ? 'text-[#C97B5E]' : 'text-purple-500'}`} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={`font-semibold text-sm truncate ${isSelected ? 'text-[#3F5728]' : 'text-[#2B2B2B]'}`}>{pet.name}</p>
                        <p className="text-xs text-[#8A8676] truncate">{pet.owner?.name} • {pet.breed}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-[#B0A99A]">{examCount} muayene</p>
                        <p className="text-xs text-[#B0A99A]">{vaccCount} aşı</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Sağ: Rapor */}
        <div className="lg:col-span-2">
          {!selectedPet ? (
            <div className="dribbble-panel p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-[#E8EFD9] flex items-center justify-center mx-auto mb-4">
                <FileText className="w-8 h-8 text-teal-400" />
              </div>
              <h3 className="text-lg font-semibold text-[#2B2B2B]">Hasta Seçin</h3>
              <p className="text-[#B0A99A] text-sm mt-1">Sol listeden bir hasta seçerek geçmişini görüntüleyin</p>
            </div>
          ) : (
            <motion.div ref={printRef} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">

              {/* PDF'de görünür, ekranda gizli başlık */}
              <div className="pdf-only-header" style={{ display: 'none', textAlign: 'center', paddingBottom: '16px', borderBottom: '2px solid #e5e7eb', marginBottom: '8px' }}>
                <h1 style={{ fontSize: '20px', fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>
                  Hasta Gecmisi Raporu - {selectedPet.name}
                </h1>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                  Olusturulma: {new Date().toLocaleDateString('tr-TR')} • KlinikVet Yonetim Sistemi
                </p>
              </div>

              {/* Hasta Bilgi Kartı */}
              <div className="dribbble-panel p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${selectedPet.species === 'dog' ? 'bg-[#FAECE7]' : 'bg-[#F0EBE1]'}`}>
                    <PawPrint className={`w-7 h-7 ${selectedPet.species === 'dog' ? 'text-[#C97B5E]' : 'text-purple-500'}`} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-[#2B2B2B]">{selectedPet.name}</h2>
                    <p className="text-[#8A8676] text-sm">{translateSpecies(selectedPet.species)} • {selectedPet.breed}</p>
                  </div>
                  {selectedPet.protocolNo && (
                    <span className="ml-auto px-3 py-1 bg-[#E8EFD9] text-[#3F5728] text-sm rounded-full border border-[#D2DEB4]">
                      Çip: {selectedPet.protocolNo}
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-[#F0EBE1]">
                  <div>
                    <p className="text-xs text-[#B0A99A] uppercase tracking-wide">Yaş</p>
                    <p className="font-semibold text-[#2B2B2B] mt-0.5">{displayAge(selectedPet.age)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#B0A99A] uppercase tracking-wide">Cinsiyet</p>
                    <p className="font-semibold text-[#2B2B2B] mt-0.5">{selectedPet.gender === 'male' ? 'Erkek' : 'Dişi'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#B0A99A] uppercase tracking-wide">Kilo</p>
                    <p className="font-semibold text-[#2B2B2B] mt-0.5">{selectedPet.weight ? `${selectedPet.weight} kg` : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#B0A99A] uppercase tracking-wide">Sahip</p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <User className="w-3.5 h-3.5 text-[#B0A99A]" />
                      <p className="font-semibold text-[#2B2B2B] text-sm">{selectedPet.owner?.name || '-'}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Özet İstatistikler */}
              <div className="grid grid-cols-3 gap-3">
                <div className="dribbble-panel p-4 text-center">
                  <Stethoscope className="w-5 h-5 text-[#557236] mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2B2B2B]">{petExaminations.length}</p>
                  <p className="text-xs text-[#8A8676]">Muayene</p>
                </div>
                <div className="dribbble-panel p-4 text-center">
                  <Syringe className="w-5 h-5 text-green-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2B2B2B]">{petVaccinations.length}</p>
                  <p className="text-xs text-[#8A8676]">Aşı</p>
                </div>
                <div className="dribbble-panel p-4 text-center">
                  <Calendar className="w-5 h-5 text-purple-500 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-[#2B2B2B]">{petAppointments.length}</p>
                  <p className="text-xs text-[#8A8676]">Randevu</p>
                </div>
              </div>

              {/* Muayeneler */}
              <div className="dribbble-panel overflow-hidden">
                <button
                  onClick={() => toggleSection('examinations')}
                  className="no-print w-full flex items-center justify-between p-4 border-b border-[#F0EBE1] hover:bg-[#F7F4ED] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#E8EFD9] flex items-center justify-center">
                      <Stethoscope className="w-4 h-4 text-[#557236]" />
                    </div>
                    <span className="font-semibold text-[#2B2B2B]">Muayene Geçmişi</span>
                    <span className="px-2 py-0.5 bg-[#E8EFD9] text-[#3F5728] text-xs rounded-full">{petExaminations.length}</span>
                  </div>
                  {expandedSections.examinations ? <ChevronUp className="w-4 h-4 text-[#B0A99A]" /> : <ChevronDown className="w-4 h-4 text-[#B0A99A]" />}
                </button>
                {expandedSections.examinations && (
                  petExaminations.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-[#F7F4ED]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Tarih</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Belirtiler</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Teşhis</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Tedavi</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0EBE1]">
                        {petExaminations.map(ex => (
                          <tr key={ex.id} className="hover:bg-[#F7F4ED]">
                            <td className="px-4 py-3 text-sm text-[#8A8676] whitespace-nowrap">{formatDate(ex.date)}</td>
                            <td className="px-4 py-3 text-sm text-[#2B2B2B]">{ex.symptoms || '-'}</td>
                            <td className="px-4 py-3 text-sm text-[#2B2B2B]">{ex.diagnosis || '-'}</td>
                            <td className="px-4 py-3 text-sm text-[#2B2B2B]">{ex.treatment || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="p-6 text-center text-[#B0A99A] text-sm">Muayene kaydı bulunmuyor</p>
                  )
                )}
              </div>

              {/* Aşılar */}
              <div className="dribbble-panel overflow-hidden">
                <button
                  onClick={() => toggleSection('vaccinations')}
                  className="no-print w-full flex items-center justify-between p-4 border-b border-[#F0EBE1] hover:bg-[#F7F4ED] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                      <Syringe className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="font-semibold text-[#2B2B2B]">Aşı Geçmişi</span>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">{petVaccinations.length}</span>
                  </div>
                  {expandedSections.vaccinations ? <ChevronUp className="w-4 h-4 text-[#B0A99A]" /> : <ChevronDown className="w-4 h-4 text-[#B0A99A]" />}
                </button>
                {expandedSections.vaccinations && (
                  petVaccinations.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-[#F7F4ED]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Tarih</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Aşı Adı</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Sonraki Doz</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Durum</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Notlar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0EBE1]">
                        {petVaccinations.map(v => (
                          <tr key={v.id} className="hover:bg-[#F7F4ED]">
                            <td className="px-4 py-3 text-sm text-[#8A8676] whitespace-nowrap">{formatDate(v.date)}</td>
                            <td className="px-4 py-3 text-sm font-medium text-[#2B2B2B]">{v.vaccine_name || v.name || '-'}</td>
                            <td className="px-4 py-3 text-sm text-[#8A8676] whitespace-nowrap">{formatDate(v.next_due_date || v.nextDueDate)}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-[#FAECE7] text-amber-700'}`}>
                                {v.status === 'completed' ? 'Tamamlandı' : 'Planlandı'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#8A8676]">{v.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="p-6 text-center text-[#B0A99A] text-sm">Aşı kaydı bulunmuyor</p>
                  )
                )}
              </div>

              {/* Randevular */}
              <div className="dribbble-panel overflow-hidden">
                <button
                  onClick={() => toggleSection('appointments')}
                  className="no-print w-full flex items-center justify-between p-4 border-b border-[#F0EBE1] hover:bg-[#F7F4ED] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-[#F0EBE1] flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-purple-600" />
                    </div>
                    <span className="font-semibold text-[#2B2B2B]">Randevu Geçmişi</span>
                    <span className="px-2 py-0.5 bg-[#F0EBE1] text-[#8A8676] text-xs rounded-full">{petAppointments.length}</span>
                  </div>
                  {expandedSections.appointments ? <ChevronUp className="w-4 h-4 text-[#B0A99A]" /> : <ChevronDown className="w-4 h-4 text-[#B0A99A]" />}
                </button>
                {expandedSections.appointments && (
                  petAppointments.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-[#F7F4ED]">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Tarih</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Saat</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Tür</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Durum</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-[#8A8676] uppercase">Notlar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#F0EBE1]">
                        {petAppointments.map(a => (
                          <tr key={a.id} className="hover:bg-[#F7F4ED]">
                            <td className="px-4 py-3 text-sm text-[#8A8676] whitespace-nowrap">{formatDate(a.date)}</td>
                            <td className="px-4 py-3 text-sm text-[#8A8676]">{a.time || '-'}</td>
                            <td className="px-4 py-3 text-sm text-[#2B2B2B] capitalize">{a.type || '-'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                a.status === 'completed' ? 'bg-green-100 text-green-700' :
                                a.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                                'bg-[#E8EFD9] text-[#3F5728]'
                              }`}>
                                {getStatusLabel(a.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-[#8A8676] max-w-xs truncate">{a.notes || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="p-6 text-center text-[#B0A99A] text-sm">Randevu kaydı bulunmuyor</p>
                  )
                )}
              </div>

              {/* Zaman Çizelgesi */}
              {timeline.length > 0 && (
                <div className="dribbble-panel p-6">
                  <h3 className="font-semibold text-[#2B2B2B] mb-4 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-[#8A8676]" />
                    Kronolojik Zaman Çizelgesi
                  </h3>
                  <div className="space-y-3">
                    {timeline.map((item, i) => (
                      <div key={i} className="flex gap-4 items-start">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5
                          ${item.type === 'examination' ? 'bg-[#E8EFD9]' : item.type === 'vaccination' ? 'bg-green-100' : 'bg-[#F0EBE1]'}">
                          {item.type === 'examination' && <Stethoscope className="w-4 h-4 text-[#557236]" />}
                          {item.type === 'vaccination' && <Syringe className="w-4 h-4 text-green-600" />}
                          {item.type === 'appointment' && <Calendar className="w-4 h-4 text-purple-600" />}
                        </div>
                        <div className="flex-1 pb-3 border-b border-[#F0EBE1] last:border-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-[#2B2B2B]">
                              {item.type === 'examination' && `Muayene - ${(item.data as Examination).diagnosis || 'Teşhis girilmemiş'}`}
                              {item.type === 'vaccination' && `Aşı - ${(item.data as Vaccination).vaccine_name || (item.data as Vaccination).name}`}
                              {item.type === 'appointment' && `Randevu - ${(item.data as Appointment).type}`}
                            </p>
                            <span className="text-xs text-[#B0A99A] ml-4 flex-shrink-0">{formatDate(item.date)}</span>
                          </div>
                          <p className="text-xs text-[#8A8676] mt-0.5">
                            {item.type === 'examination' && (item.data as Examination).treatment}
                            {item.type === 'vaccination' && `Sonraki doz: ${formatDate((item.data as Vaccination).next_due_date)}`}
                            {item.type === 'appointment' && (item.data as Appointment).notes}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientReport;
